import BasicInput from '@/components/BasicInput';
import BasicSelect from '@/components/BasicSelect';
import WarningPrompt from '@/components/WarningPrompt';

export default {
    components: { BasicInput, BasicSelect, WarningPrompt },
    props: ['csrftoken', 'ownerid', 'companyid', 'salesforceid', 'details', 'expiry', 'valid', 'errors'],
    data() {
        return {
            inverterType: this.details.inverterType,
            inverterName: this.details.inverterName,
            numberOfMPPTrackers: Number(this.details.numberOfMPPTrackers) || 1,
            inverterOptions: [
                {
                    label: 'String Inverter',
                    value: 'string',
                },
                {
                    label: 'DCU',
                    value: 'dcu',
                },
                {
                    label: 'Battery Inverter',
                    value: 'not-supported',
                },
                {
                    label: 'Solar Pump Inverter',
                    value: 'not-supported',
                },
                {
                    label: 'Hybrid Inverter',
                    value: 'string',
                },
                {
                    label: 'Microinverter',
                    value: 'micro',
                },
                {
                    label: 'PV Water Heater',
                    value: 'not-supported',
                },
            ]
        }
    },
    computed: {
        hideMppts() {
            return this.inverterType == 'micro';
        },
        formErrors() {
            return !this.valid && this.errors ? JSON.parse(this.errors) : null;
        },
        inverterTypeNotSupported() {
            const types = ['Battery Inverter', 'Solar Pump Inverter', 'PV Water Heater'];
            return types.includes(this.inverterName);
        }
    },

    methods: {
        getError(field) {
            if (this.formErrors && field in this.formErrors) {
                return 'This field is required';
            }

            return null;
        },
        updateNumberOfMPPTrackers(value) {
            this.numberOfMPPTrackers = value;
        }
    },

    template: `
        <div>
            <form method="POST">
                <input type="hidden" name="action" value="e7project/product" />
                <input type="hidden" name="CRAFT_CSRF_TOKEN" :value="csrftoken" />
                <input type="hidden" name="method" value="update" />
                <input type="hidden" name="ownerId" :value="ownerid" />
                <input type="hidden" name="companyId" :value="companyid" />
                <input type="hidden" name="salesforceId" :value="salesforceid" />
                <input v-if="!details.inverterName" type="hidden" name="details[inverterName]" :value="inverterName" />
                <input type="hidden" name="details[inverterType]" :value="inverterType" />

                <WarningPrompt v-if="!valid" text="This product is missing required fields. The missing fields highlighted below must be completed before this product can be used in the creation of a job." />

                <div class="fields relative">
                    <BasicSelect v-if="!details.inverterName" label="Inverter Type" name="inverterTypeName" placeholder="Select" :error="getError('inverterType')" :options="inverterOptions" :required="true" @updated="inverterType = $event.value; inverterName = $event.label; numberOfMPPTrackers = 1" />
                    <BasicInput v-else label="Inverter Type" name="details[inverterName]" :value="inverterName" :required="true" :readonly="true" />
                    <p v-if="inverterTypeNotSupported" class="error--absolute error text--orange small semi">
                        This inverter type is not currently supported in My Jobs
                    </p>
                </div>

                <h3 class="h4 h-padding-lg">Input (DC)</h3>
                <div class="fields">
                    <BasicInput label="Max DC Input" name="details[maxDCWatts]" :value="details.maxDCWatts" unit="W" :required="true" :error="getError('maxDCWatts')" :readonly="Boolean(details.maxDCWatts)" type="number" step="0.001" />
                    <div class="break"></div>

                    <BasicInput label="DC input voltage range - lower" name="details[dcInputVoltageRangeLower]" :value="details.dcInputVoltageRangeLower" unit="V" :required="true" :error="getError('dcInputVoltageRangeLower')" :readonly="Boolean(details.dcInputVoltageRangeLower)" type="number" step="0.001" />
                    <BasicInput label="DC input voltage range - upper" name="details[dcInputVoltageRangeUpper]" :value="details.dcInputVoltageRangeUpper" unit="V" :required="true" :error="getError('dcInputVoltageRangeUpper')" :readonly="Boolean(details.dcInputVoltageRangeUpper)" type="number" step="0.001" />
                    <BasicInput label="MPP voltage range - lower" name="details[minMPPVoltage]" :value="details.minMPPVoltage" unit="V" :required="true" :error="getError('minMPPVoltage')" :readonly="Boolean(details.minMPPVoltage)" type="number" step="0.001" />
                    <BasicInput label="MPP voltage range - upper" name="details[maxInputVoltage]" :value="details.maxInputVoltage" unit="V" :required="true" :error="getError('maxInputVoltage')" :readonly="Boolean(details.maxInputVoltage)" type="number" step="0.001" />

                    <template v-if="!hideMppts">
                        <BasicSelect v-if="!details.numberOfMPPTrackers" label="Number of MPP Trackers" name="details[numberOfMPPTrackers]" :value="numberOfMPPTrackers" :options="[1,2,3,4,5,6]" :required="true" @updated="updateNumberOfMPPTrackers" />
                        <BasicInput v-else label="Number of MPP Trackers" name="details[numberOfMPPTrackers]" :value="numberOfMPPTrackers" :readonly="true" />
                        <div class="break"></div>
                    </template>

                    <template v-for="n in numberOfMPPTrackers">
                        <BasicInput :label="'Max input current - MPPT ' + n" name="details[maxInputCurrent][]" :value="details.maxInputCurrent[n - 1]" :required="true" :error="getError('maxInputCurrent')" unit="A" type="number" step="0.001" :readonly="Boolean(details.maxInputCurrent[n - 1])" />
                        <BasicInput :label="'Max short circuit current - MPPT ' + n" name="details[maxShortCircuitCurrent][]" :value="details.maxShortCircuitCurrent[n - 1]" :required="true" :error="getError('maxShortCircuitCurrent')" unit="A" type="number" step="0.001" :readonly="Boolean(details.maxShortCircuitCurrent[n - 1])" />
                    </template>

                    <BasicInput label="Connector Manufacturer" name="details[connectorManufacturer]" :value="details.connectorManufacturer" :readonly="Boolean(details.connectorManufacturer)" />
                    <BasicInput label="Connector Type" name="details[connectorType]" :value="details.connectorType" :readonly="Boolean(details.connectorType)" />
                </div>

                <h3 class="h4 h-padding-lg">Output (AC)</h3>
                <div class="fields">
                    <BasicInput label="Nominal output power" name="details[nominalOutputPower]" :value="details.nominalOutputPower" :required="true" :error="getError('nominalOutputPower')" unit="VA" :readonly="Boolean(details.nominalOutputPower)" type="number" step="0.001" tooltip="Check VA rating of inverter against CEC Approved Product listing for STCs" />
                    <BasicInput label="Max output power" name="details[maxOutputPower]" :value="details.maxOutputPower" :required="true" :error="getError('maxOutputPower')" unit="W" :readonly="Boolean(details.maxOutputPower)" type="number" step="0.001" />
                    <BasicInput label="AC output current" name="details[acOutputCurrent]" :value="details.acOutputCurrent" :required="true" :error="getError('acOutputCurrent')" unit="A" :readonly="Boolean(details.acOutputCurrent)" type="number" step="0.001" />

                    <BasicSelect v-if="!details.numberOfPhases" label="Number of Phases" name="details[numberOfPhases]" :value="details.numberOfPhases" :options="[1,2,3]" :required="true" :error="getError('numberOfPhases')" />
                    <BasicInput v-else label="Number of Phases" name="details[numberOfPhases]" :value="details.numberOfPhases" :readonly="true" />

                    <BasicInput label="RCD Type" name="details[rcdType]" :value="details.rcdType" :readonly="Boolean(details.rcdType)" tooltip="Different RCD (residual current device) Types work with different inverters. Check your inverter manual" />
                </div>

                <h3 class="h4 h-padding-lg">General</h3>
                <div class="fields">
                    <BasicInput label="Inverter Dimensions: Depth" name="details[depth]" :value="details.depth" unit="mm" :readonly="Boolean(details.depth)" type="number" step="0.001" />
                    <BasicInput label="Inverter Dimensions: Width" name="details[width]" :value="details.width" unit="mm" :readonly="Boolean(details.width)" type="number" step="0.001" />
                    <BasicInput label="Inverter Dimensions: Height" name="details[height]" :value="details.height" unit="mm" :readonly="Boolean(details.height)" type="number" step="0.001" />
                    <div class="break"></div>

                    <BasicInput label="Inverter Clearance: Above" name="details[clearanceAbove]" :value="details.clearanceAbove" unit="mm" :readonly="Boolean(details.clearanceAbove)" type="number" step="0.001" />
                    <BasicInput label="Inverter Clearance: Sides" name="details[clearanceSides]" :value="details.clearanceSides" unit="mm" :readonly="Boolean(details.clearanceSides)" type="number" step="0.001" />
                    <BasicInput label="Inverter Clearance: Below" name="details[clearanceBelow]" :value="details.clearanceBelow" unit="mm" :readonly="Boolean(details.clearanceBelow)" type="number" step="0.001" />
                    <div class="break"></div>

                    <BasicInput label="Weight" name="details[weight]" :value="details.weight" unit="kg" :readonly="Boolean(details.weight)" type="number" step="0.001"  />
                    <BasicInput label="IP rating" name="details[ipRating]" :value="details.ipRating" :readonly="Boolean(details.ipRating)" />

                    <BasicSelect v-if="!details.topology" label="Topology" name="details[topology]" :options="['transformer', 'transformerless']" :value="details.topology" :required="true" />
                    <BasicInput v-else label="Topology" name="details[topology]" :value="details.topology" :required="true" :readonly="true" />

                    <BasicInput label="Ambient Temp Min" name="details[ambientTempMin]" :value="details.ambientTempMin" unit="&#176;C" :readonly="Boolean(details.ambientTempMin)" type="number" step="0.001" />
                    <BasicInput label="Ambient Temp Max" name="details[ambientTempMax]" :value="details.ambientTempMax" unit="&#176;C" :readonly="Boolean(details.ambientTempMax)" type="number" step="0.001" />
                </div>

                <h3 class="h4 h-padding-lg">Efficiency</h3>
                <div class="fields">
                    <BasicInput label="Maximum Efficiency" name="details[efficiency]" :value="details.efficiency" unit="%" :required="true" :readonly="Boolean(details.efficiency)" :error="getError('efficiency')"  type="number" step="any" min="0" max="100" />
                    <BasicInput label="European Efficiency" name="details[europeanEfficiency]" :value="details.europeanEfficiency" unit="%" :readonly="Boolean(details.europeanEfficiency)" type="number" step="1" min="0" max="100" tooltip="Call the product manufacturer if this value is not provided on the datasheet"/>
                </div>

                <h3 class="h4 h-padding-lg">Expiry</h3>
                <div class="fields">
                    <BasicInput label="CEC listing expiry date" name="expiry" :value="expiry" :readonly="true" />
                </div>

                <p class="small mt-lg mb-md">While all care has been taken to ensure the My Jobs solution is free from omission and error, the Clean Energy Council takes no responsibility for the use of this information in the design and/or installation of any solar PV system. Please see our <a class="link" href="/legal" target="_blank">Website User Terms and Conditions</a>.</p>

                <button class="btn btn--medium">Update inverter</button>
            </form>
            <form method="POST">
                <input type="hidden" name="action" value="e7project/product" />
                <input type="hidden" name="CRAFT_CSRF_TOKEN" :value="csrftoken" />
                <input type="hidden" name="method" value="delete" />
                <input type="hidden" name="ownerId" :value="ownerid" />
                <input type="hidden" name="companyId" :value="companyid" />
                <input type="hidden" name="salesforceId" :value="salesforceid" />
                <div class="favourite-product-delete ml-md">
                    <button class="btn btn--ghost-thin btn--medium">Delete Inverter</button>
                </div>
            </form>
        </div>
    `,
};
